/**
 * Oliver Theme System
 *
 * Ported from @oliver_codebase/src/components/chat/ThemePicker.jsx
 * Single source of truth for all Oliver theme definitions in the CRM.
 * Do NOT duplicate color values — import from here everywhere.
 */

export const OLIVER_THEMES = [
  {
    name: "Warm Amber",
    value: "amber",
    gradient:
      "linear-gradient(135deg, #fdf6f0 0%, #fef9f5 40%, #f9ede0 70%, #f5e6d3 100%)",
    accent: "#c2713a",
    accentLight: "#fdf0e6",
    accentBorder: "#e8b48a",
    orbColors: ["#f5c89a", "#c0622a", "#8f3d14"],
  },
  {
    name: "Sage Green",
    value: "sage",
    gradient:
      "linear-gradient(135deg, #f2f7f4 0%, #eef5f0 40%, #deeee5 70%, #cfe4d8 100%)",
    accent: "#3a7a58",
    accentLight: "#e8f5ee",
    accentBorder: "#8ec4a8",
    orbColors: ["#8fcfaa", "#2d7a50", "#1a5235"],
  },
  {
    name: "Sky Blue",
    value: "sky",
    gradient:
      "linear-gradient(135deg, #f0f6fd 0%, #e8f2fb 40%, #d6e9f8 70%, #c3ddf4 100%)",
    accent: "#2a7bc0",
    accentLight: "#e4f1fb",
    accentBorder: "#82bde8",
    orbColors: ["#7bbfe8", "#1a6fab", "#0f4a7a"],
  },
  {
    name: "Lavender",
    value: "lavender",
    gradient:
      "linear-gradient(135deg, #f5f2fd 0%, #f0edfb 40%, #e4dcf8 70%, #d7cef5 100%)",
    accent: "#6b4ec9",
    accentLight: "#ede8fb",
    accentBorder: "#b09ee8",
    orbColors: ["#b09ee8", "#5040a0", "#2d2570"],
  },
  {
    name: "Rose",
    value: "rose",
    gradient:
      "linear-gradient(135deg, #fdf2f4 0%, #fbeef1 40%, #f6dde3 70%, #f1cdd6 100%)",
    accent: "#c2446a",
    accentLight: "#fde8ef",
    accentBorder: "#e89ab6",
    orbColors: ["#e89aaa", "#a02840", "#6b1228"],
  },
  {
    name: "Slate",
    value: "slate",
    gradient:
      "linear-gradient(135deg, #f4f5f7 0%, #eef0f3 40%, #e2e6ec 70%, #d5dbe5 100%)",
    accent: "#4a5568",
    accentLight: "#edf0f4",
    accentBorder: "#a0aec0",
    orbColors: ["#9aaac0", "#304060", "#1a2535"],
  },
];

export const DEFAULT_THEME = "amber";

// Applied when the account has no preference of its own.
export const DEFAULT_DARK_MODE = false;

/**
 * Returns the full theme object for a given theme value.
 * Falls back to Warm Amber when the value is unknown.
 */
export function getThemeColors(themeValue) {
  return OLIVER_THEMES.find((t) => t.value === themeValue) || OLIVER_THEMES[0];
}

/**
 * Returns [primary, secondary, tertiary] hex colors for the Orb component.
 */
export function getThemeOrbColors(themeValue) {
  return getThemeColors(themeValue).orbColors;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function darkenHex(hex, factor) {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Composites `hex` over an opaque `baseHex` at `alpha`, returning an OPAQUE
 * rgb string. Used for surfaces that sit over scrolling content and therefore
 * can't be translucent, but should still carry an accent tint.
 */
function tintOverHex(hex, baseHex, alpha) {
  const mix = (c, i) => {
    const top = parseInt(hex.slice(i, i + 2), 16);
    const bottom = parseInt(baseHex.slice(i, i + 2), 16);
    return Math.round(bottom * (1 - alpha) + top * alpha);
  };
  return `rgb(${mix(hex, 1)}, ${mix(hex, 3)}, ${mix(hex, 5)})`;
}

/**
 * Builds the complete set of CSS custom properties for the Oliver UI.
 *
 * These are injected onto `.chat-bot-popover` (the outermost Oliver element)
 * and cascade down through every Oliver child component via var(--oliver-*).
 * Nothing leaks into the host CRM because the variables are scoped to that
 * subtree — child components reference them without needing any prop drilling.
 *
 * @param {string} themeValue - One of OLIVER_THEMES[*].value
 * @param {boolean} darkMode
 * @returns {Object} Plain object suitable for Vue :style binding
 */
export function buildThemeVars(themeValue, darkMode) {
  const tc = getThemeColors(themeValue);

  if (darkMode) {
    return {
      "--oliver-accent": tc.accent,
      "--oliver-accent-light": tc.accentLight,
      "--oliver-accent-border": hexToRgba(tc.accent, 0.45),
      "--oliver-gradient": "hsl(25, 15%, 10%)",
      "--oliver-ring-gradient": `linear-gradient(135deg, hsl(25, 15%, 10%) 0%, ${tc.accent} 50%, hsl(25, 15%, 14%) 100%)`,
      "--oliver-bg": "#1d1916",
      "--oliver-panel-bg": "rgba(29, 25, 22, 0.97)",
      "--oliver-bg-surface": "rgba(255,255,255,0.08)",
      "--oliver-suggestions-bg":
        "linear-gradient(160deg, rgb(253,248,244) 0%, rgb(255,249,246) 100%)",
      "--oliver-input-bg": "rgba(255,255,255,0.08)",
      "--oliver-input-border": "rgba(255,255,255,0.15)",
      "--oliver-input-focus-border": hexToRgba(tc.accent, 0.55),
      "--oliver-messages-bg": "#1d1916",
      // Opaque, accent-tinted pill fill for the patient-mode banner. Kept dark
      // so the light --oliver-text stays readable, but carries the theme's
      // accent so the banner matches the active theme. Must be opaque because
      // messages scroll under the pill (a translucent fill killed contrast).
      "--oliver-chip-bg": tintOverHex(tc.accent, "#1d1916", 0.22),
      "--oliver-text": "#f1f5f9",
      "--oliver-text-muted": "#94a3b8",
      "--oliver-border": "rgba(255,255,255,0.1)",
      "--oliver-user-bubble-bg": darkenHex(tc.accent, 0.55),
      "--oliver-user-bubble-border": darkenHex(tc.accent, 0.45),
      "--oliver-user-bubble-text": "#f1f5f9",
      "--oliver-user-bubble-text-muted": "rgba(255,255,255,0.5)",
      "--oliver-assistant-bg": "rgba(255,255,255,0.08)",
      // Opaque, for what floats above the answer: the citation card.
      "--oliver-assistant-solid": "#2a2420",
      "--oliver-assistant-shadow": "0 2px 8px rgba(0,0,0,0.06)",
      "--oliver-chart-plate": "#f7f5f2",
      "--oliver-chart-plate-shadow": "0 2px 14px rgba(0,0,0,0.45)",
      "--oliver-header-bg": "transparent",
      "--oliver-btn-subtle-bg": tc.accentLight,
      "--oliver-btn-subtle-hover": hexToRgba(tc.accent, 0.2),
      "--oliver-btn-subtle-color": tc.accent,
      "--oliver-scrollbar": "rgba(255,255,255,0.25)",
      "--oliver-indicator-bg": hexToRgba(tc.accent, 0.12),
      "--oliver-indicator-border": hexToRgba(tc.accent, 0.25),
      "--oliver-code-bg": "rgba(255,255,255,0.08)",
      "--oliver-toggle-off-bg": "rgba(255,255,255,0.08)",
      "--oliver-toggle-off-gap": "#3b3734",
      "--oliver-table-border": "rgba(255,255,255,0.15)",
      "--oliver-table-header-bg": "rgba(255,255,255,0.05)",
      "--oliver-link-color": tc.accentBorder,
      "--oliver-greeting-subtitle": "#fff9",
      "--oliver-success": "#34d399",
      "--oliver-success-soft": "rgba(52, 211, 153, 0.16)",
    };
  }

  return {
    "--oliver-accent": tc.accent,
    "--oliver-accent-light": tc.accentLight,
    "--oliver-accent-border": tc.accentBorder,
    "--oliver-gradient": tc.gradient,
    "--oliver-ring-gradient": `linear-gradient(135deg, ${tc.accentLight} 0%, ${tc.accent} 50%, ${tc.accentBorder} 100%)`,
    "--oliver-bg": "rgba(255,255,255,0.85)",
    "--oliver-panel-bg": "rgba(255, 255, 255, 0.97)",
    "--oliver-bg-surface": "rgba(255,255,255,0.8)",
    "--oliver-suggestions-bg":
      "linear-gradient(160deg, #fdf8f4 0%, #fff9f6 100%)",
    "--oliver-input-bg": "rgba(255,255,255,0.85)",
    "--oliver-input-border": "rgba(0,0,0,0.12)",
    "--oliver-input-focus-border": hexToRgba(tc.accent, 0.45),
    "--oliver-messages-bg": "rgba(255,255,255,0.0)",
    // Opaque, accent-tinted light pill fill for the patient-mode banner —
    // uses the theme's light accent tint so the banner reflects the active
    // theme, paired with the dark --oliver-text below.
    "--oliver-chip-bg": tc.accentLight,
    "--oliver-text": "hsl(20, 15%, 12%)",
    "--oliver-text-muted": "rgba(0,0,0,0.6)",
    "--oliver-border": "rgba(0,0,0,0.08)",
    "--oliver-user-bubble-bg": darkenHex(tc.accent, 0.55),
    "--oliver-user-bubble-border": darkenHex(tc.accent, 0.45),
    "--oliver-user-bubble-text": "#ffffff",
    "--oliver-user-bubble-text-muted": "rgba(255,255,255,0.5)",
    "--oliver-assistant-bg": "rgba(255,255,255,0.95)",
    "--oliver-assistant-solid": "#ffffff",
    "--oliver-assistant-shadow": "0 2px 8px rgba(0,0,0,0.06)",
    "--oliver-chart-plate": "#ffffff",
    "--oliver-chart-plate-shadow": "0 1px 3px rgba(60,40,20,0.1)",
    "--oliver-header-bg": "transparent",
    "--oliver-btn-subtle-bg": tc.accentLight,
    "--oliver-btn-subtle-hover": hexToRgba(tc.accent, 0.15),
    "--oliver-btn-subtle-color": tc.accent,
    "--oliver-scrollbar": hexToRgba(tc.accent, 0.3),
    "--oliver-indicator-bg": hexToRgba(tc.accent, 0.08),
    "--oliver-indicator-border": hexToRgba(tc.accent, 0.2),
    "--oliver-code-bg": "rgba(0,0,0,0.08)",
    "--oliver-toggle-off-bg": "rgba(0,0,0,0.06)",
    "--oliver-toggle-off-gap": "#f3f3f3",
    "--oliver-table-border": "rgba(0,0,0,0.1)",
    "--oliver-table-header-bg": "rgba(0,0,0,0.05)",
    "--oliver-link-color": "#0066cc",
    "--oliver-greeting-subtitle": "#231d1a99",
    "--oliver-success": "#0f9d76",
    "--oliver-success-soft": "rgba(15, 157, 118, 0.14)",
  };
}
